import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { ArrowUpRight } from "lucide-react";

const initial = { name: "", email: "", phone: "", portfolio_size: "", message: "" };

export default function ConsultationForm({ onSubmit }) {
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Required";
    if (!form.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Invalid email";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Please provide at least 10 characters";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    const payload = {
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim() || null,
      portfolio_size: form.portfolio_size.trim() || null,
      message: form.message.trim(),
    };
    const ok = await onSubmit(payload);
    setSubmitting(false);
    if (ok) setForm(initial);
  };

  const inputCls =
    "w-full bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 text-base text-[#F8F9FA] placeholder:text-[#A0AAB2]/60 focus-visible:ring-0 focus-visible:border-[#C5A059] focus:outline-none transition-colors";

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="consultation-form"
      className="bg-[#151A22] border border-white/10 p-8 sm:p-12"
      noValidate
    >
      <p className="overline mb-2">Confidential enquiry</p>
      <h3 className="font-serif text-3xl mb-10 leading-tight">Tell us about your portfolio.</h3>

      <div className="grid sm:grid-cols-2 gap-x-8 gap-y-7">
        <Field label="Full name" error={errors.name}>
          <Input
            data-testid="form-input-name"
            value={form.name}
            onChange={update("name")}
            className={inputCls}
            placeholder="Your name"
            autoComplete="name"
          />
        </Field>
        <Field label="Email" error={errors.email}>
          <Input
            data-testid="form-input-email"
            type="email"
            value={form.email}
            onChange={update("email")}
            className={inputCls}
            placeholder="you@domain.com"
            autoComplete="email"
          />
        </Field>
        <Field label="Phone (optional)">
          <Input
            data-testid="form-input-phone"
            value={form.phone}
            onChange={update("phone")}
            className={inputCls}
            placeholder="+1 555 000 0000"
            autoComplete="tel"
          />
        </Field>
        <Field label="Portfolio size (optional)">
          <Input
            data-testid="form-input-portfolio"
            value={form.portfolio_size}
            onChange={update("portfolio_size")}
            className={inputCls}
            placeholder="e.g. 3 units, London"
          />
        </Field>
        <div className="sm:col-span-2">
          <Field label="How can we help?" error={errors.message}>
            <Textarea
              data-testid="form-input-message"
              value={form.message}
              onChange={update("message")}
              className={`${inputCls} min-h-[120px] resize-none py-3`}
              placeholder="Briefly describe your assets and the outcome you are seeking."
              rows={5}
            />
          </Field>
        </div>
      </div>

      <button
        type="submit"
        disabled={submitting}
        data-testid="form-submit-button"
        className="mt-10 group inline-flex items-center gap-3 bg-[#C5A059] text-[#0B0E14] px-7 py-4 hover:bg-[#D4AF37] transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className="font-medium tracking-wide text-sm uppercase">
          {submitting ? "Sending…" : "Submit request"}
        </span>
        <ArrowUpRight
          size={18}
          className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </button>

      <p className="mt-6 text-xs text-[#A0AAB2]/80 leading-relaxed">
        By submitting you agree to a private follow-up. Grand Venture FZ LLC is strictly B2B and does not
        handle client funds.
      </p>
    </form>
  );
}

function Field({ label, error, children }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-[0.22em] text-[#A0AAB2] mb-2">
        {label}
      </span>
      {children}
      {error && (
        <span
          data-testid="form-field-error"
          className="block mt-2 text-xs text-red-400"
        >
          {error}
        </span>
      )}
    </label>
  );
}
